import { Navigate, useLocation } from 'react-router-dom';
import { ReactNode, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { refreshAccessToken } from '@/lib/api';
import { isTokenExpired, useAccessToken } from '@/lib/session';

interface ProtectedRouteProps {
  children: ReactNode;
}

// Checking that a token EXISTS is not checking that it works. This used to let an expired token
// straight through, so a tab left open overnight looked signed in while every request behind it
// failed — and those failures were misread further down as "no workspace", landing the user on
// /onboarding. Now a lapsed token is renewed before anything renders, and a session that is over
// goes to /login, carrying the page so signing in returns to it.
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const token = useAccessToken();
  const location = useLocation();
  const [unreachable, setUnreachable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Once in, stay in: a token that lapses while the page is open is renewed in the background (and
  // by authFetch on the next request) rather than unmounting the page, and whatever was typed into
  // it, behind a spinner.
  const [entered, setEntered] = useState(false);

  const lapsed = !!token && isTokenExpired(token);

  useEffect(() => {
    if (token && !lapsed) setEntered(true);
  }, [token, lapsed]);

  useEffect(() => {
    if (!lapsed) return;
    let active = true;
    setUnreachable(false);
    // Success writes the new token to the store and re-renders this guard; an ended session clears
    // it, which re-renders this guard into the redirect below.
    refreshAccessToken(token).catch(() => {
      if (active) setUnreachable(true);
    });
    return () => {
      active = false;
    };
  }, [lapsed, token, attempt]);

  if (!token) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (!lapsed || entered) {
    return <>{children}</>;
  }

  if (unreachable) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-muted-foreground">Couldn't reach the server. Check your connection and try again.</p>
        <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}
